<script setup>
import { computed, onBeforeUnmount, ref, shallowRef } from 'vue';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import UiAuthLayout from '@/components/ui/UiAuthLayout.vue';
import UiFormFields from '@/components/ui/UiFormFields.vue';
import UiOptionList from '@/components/ui/UiOptionList.vue';
import UiOtpInput from '@/components/ui/UiOtpInput.vue';
import UiSteps from '@/components/ui/UiSteps.vue';
import { apiRequest, assertOk, errorMessage } from '@/api/http';
import { LEVEL_OPTIONS } from '@/constants/accounts';
import { email, matches, strongPassword } from '@/utils/validation';

// Sign up in three steps: who you are, your account, then the code sent to
// your email. Faculty pick their school level so the right (JHS/SHS) admin
// verifies them; duties like Admin or Prefect are granted later by an admin.
const STEPS = ['Your details', 'Account', 'Verify email'];
const ACCOUNT_TYPES = [
  { id: 'parent', name: 'Parent / Guardian', hint: 'Follow your child’s grades, attendance and reports.' },
  { id: 'faculty', name: 'Faculty', hint: 'Subject teachers, class advisers and school staff. An admin verifies your account.' },
];

const router = useRouter();
const store = useStore();

const step = shallowRef(0);
const error = shallowRef('');
const loading = shallowRef(false);
const done = shallowRef(''); // success message once the code is confirmed

// The landing page remembers which portal was picked (0 = parent, 1 = faculty).
const accountType = shallowRef(localStorage.getItem('userType') === '1' ? 'faculty' : 'parent');
const isFaculty = computed(() => accountType.value === 'faculty');

const detailsRef = ref(null);
const details = ref({ fname: '', mname: '', lname: '' });
const detailFields = [
  { key: 'fname', label: 'First name', required: true, autocomplete: 'given-name' },
  { key: 'mname', label: 'Middle name', placeholder: 'Optional', autocomplete: 'additional-name' },
  { key: 'lname', label: 'Last name', required: true, autocomplete: 'family-name' },
];

const accountRef = ref(null);
const account = ref({ levelScope: null, email: '', password: '', confirm: '' });
const emailTaken = shallowRef(false);
const notTaken = () => !emailTaken.value || 'An account with this email already exists.';
const accountFields = computed(() => [
  { key: 'levelScope', label: 'School level', type: 'select', required: true, items: LEVEL_OPTIONS, hidden: !isFaculty.value },
  { key: 'email', label: 'Email address', required: true, rules: [email, notTaken], placeholder: 'email@example.com', autocomplete: 'email' },
  {
    key: 'password',
    label: 'Password',
    type: 'password',
    required: true,
    rules: [strongPassword],
    autocomplete: 'new-password',
    description: 'At least 8 characters with upper and lower case letters, a number and a symbol.',
  },
  { key: 'confirm', label: 'Confirm password', type: 'password', required: true, autocomplete: 'new-password', rules: [matches(() => account.value.password, 'Passwords do not match.')] },
]);

async function checkEmail(value) {
  emailTaken.value = false;
  if (email(value) !== true || !value) return;
  try {
    emailTaken.value = !!(await apiRequest('get', `/auth/checkEmailIfExist/${encodeURIComponent(value.trim())}`));
  } catch {
    emailTaken.value = false;
  }
  accountRef.value?.validate();
}

// Re-check the email whenever it changes (debounced).
let emailTimer = null;
const accountValues = computed({
  get: () => account.value,
  set: (v) => {
    if (v.email !== account.value.email) {
      clearTimeout(emailTimer);
      emailTimer = setTimeout(() => checkEmail(v.email), 400);
    }
    account.value = v;
  },
});

async function nextFromDetails() {
  const { valid } = await detailsRef.value.validate();
  if (valid) step.value = 1;
}

async function createAccount() {
  const { valid } = await accountRef.value.validate();
  if (!valid) return;
  loading.value = true;
  error.value = '';
  const d = details.value;
  const a = account.value;
  try {
    const body = await apiRequest('post', '/auth/registerUser', {
      fname: d.fname.trim(),
      mname: d.mname.trim() || null,
      lname: d.lname.trim(),
      email: a.email.trim(),
      password: a.password,
      // The server gives parents the Parent role; an admin assigns faculty
      // roles after verifying them.
      accountType: isFaculty.value ? 'faculty' : 'parent',
      levelScope: isFaculty.value ? a.levelScope : null,
    });
    assertOk(body, [201]);
    store.dispatch('setEmail', a.email.trim());
    step.value = 2;
    startCooldown();
  } catch (e) {
    error.value = errorMessage(e, 'Could not create your account.');
  } finally {
    loading.value = false;
  }
}

// Email code
const code = shallowRef('');
const cooldown = shallowRef(0);
let cooldownTimer = null;

function startCooldown() {
  cooldown.value = 60;
  clearInterval(cooldownTimer);
  cooldownTimer = setInterval(() => {
    cooldown.value -= 1;
    if (cooldown.value <= 0) clearInterval(cooldownTimer);
  }, 1000);
}
onBeforeUnmount(() => {
  clearInterval(cooldownTimer);
  clearTimeout(emailTimer);
});

async function resend() {
  error.value = '';
  try {
    const body = await apiRequest('post', '/auth/resendOtp', { email: store.getters.getEmail });
    assertOk(body, [200]);
    code.value = '';
    startCooldown();
  } catch (e) {
    error.value = errorMessage(e, 'Could not resend the code.');
  }
}

async function confirmCode() {
  if (code.value.length !== 6) {
    error.value = 'Enter the 6-digit code from your email.';
    return;
  }
  loading.value = true;
  error.value = '';
  try {
    const body = await apiRequest('post', '/auth/confirmOtp', { email: store.getters.getEmail, otp: code.value });
    assertOk(body, [200]);
    // Parents can sign in now; faculty wait for an admin to verify them.
    done.value = body.msg || 'Your email is confirmed.';
  } catch (e) {
    error.value = errorMessage(e, 'That code didn’t work. Check it and try again.');
  } finally {
    loading.value = false;
  }
}

const header = computed(() => {
  if (done.value) return { title: 'You’re all set', description: done.value };
  if (step.value === 2) return { title: 'Check your email', description: `We sent a 6-digit code to ${account.value.email}.` };
  return { title: 'Create an account', description: 'Enter your details below to get started.' };
});
</script>

<template>
  <UiAuthLayout :title="header.title" :description="header.description">
    <div v-if="done" class="form-stack">
      <v-btn color="primary" block @click="router.push('/login')">Go to sign in</v-btn>
    </div>

    <div v-else class="form-stack">
      <UiSteps :steps="STEPS" :current="step" />
      <v-alert v-if="error" type="error" variant="tonal" density="compact">{{ error }}</v-alert>

      <!-- 1 · Who you are -->
      <v-form v-show="step === 0" ref="detailsRef" class="form-stack" @submit.prevent="nextFromDetails">
        <div class="field">
          <span class="field__label">I am a</span>
          <UiOptionList v-model="accountType" :items="ACCOUNT_TYPES" item-subtitle="hint" />
        </div>
        <UiFormFields v-model:values="details" :fields="detailFields" />
        <v-btn type="submit" color="primary" block>Continue</v-btn>
      </v-form>

      <!-- 2 · Account -->
      <v-form v-show="step === 1" ref="accountRef" class="form-stack" @submit.prevent="createAccount">
        <UiFormFields v-model:values="accountValues" :fields="accountFields" />
        <div class="actions">
          <v-btn variant="outlined" :disabled="loading" @click="step = 0">Back</v-btn>
          <v-btn type="submit" color="primary" class="actions__main" :loading="loading">Create account</v-btn>
        </div>
      </v-form>

      <!-- 3 · Email code -->
      <form v-if="step === 2" class="form-stack" @submit.prevent="confirmCode">
        <UiOtpInput v-model="code" :disabled="loading" @complete="confirmCode" />
        <v-btn type="submit" color="primary" block :loading="loading">Verify email</v-btn>
        <p class="resend">
          Didn’t get it?
          <button v-if="cooldown <= 0" type="button" class="link" @click="resend">Resend code</button>
          <span v-else>Resend in {{ cooldown }}s</span>
        </p>
      </form>
    </div>

    <template v-if="!done" #footer>
      Already have an account? <router-link to="/login">Sign in</router-link>
    </template>
  </UiAuthLayout>
</template>

<style scoped>
.field {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.field__label {
  font-size: 14px;
  font-weight: 500;
}
.actions {
  display: flex;
  gap: 8px;
}
.actions__main {
  flex: 1;
}
.resend {
  margin: 0;
  font-size: 14px;
  text-align: center;
  color: var(--muted-foreground);
}
.link {
  border: 0;
  padding: 0;
  background: transparent;
  color: var(--foreground);
  font: inherit;
  font-weight: 500;
  text-decoration: underline;
  text-underline-offset: 4px;
  cursor: pointer;
}
</style>
